import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button } from 'primereact/button'

import { SECTION_STATE } from '@aytm/app/stats/page/data-quality-report/lib/section-state'
import { SECTION_ANALYTICS_NAME } from '@aytm/app/stats/page/data-quality-report/lib/sections'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import TransitionPanel, { TRANSITION_DURATION } from '@aytm/app/stats/components/transition-panel/TransitionPanel'

import style from '@aytm/app/stats/page/data-quality-report/styles/SectionShell.module.scss'

const FIELDING_MESSAGE = 'Fielding in Progress'
// Exported so anything a section drives outside itself can follow the same animation (see index).
// Popup content — the dead-click guidance shown on hover over a pending section.
const renderFieldingTooltip = () => (
  <div className={style.tooltip} data-testid="stat-dqr-section-fielding-tooltip">
    {FIELDING_MESSAGE}
  </div>
)

// One accordion row: a clickable header (icon + title + summary) that toggles its body.
// Each row opens independently of its siblings — a body shows whenever its own row is expanded.
const SectionShell = ({ section, title, subtitle, icon, state, expanded, onToggle, onRetry, children }) => {
  const fielding = state === SECTION_STATE.FIELDING_IN_PROGRESS
  const loading = state === SECTION_STATE.LOADING_DATA
  const failed = state === SECTION_STATE.LOAD_FAILED
  // Every pending state greys the row and locks it: it cannot be opened and never shows a body.
  // Only fielding explains itself on hover — loading is transient and failure offers a retry instead.
  const locked = fielding || loading || failed
  const collapsed = !expanded || locked

  // The body animates shut over TRANSITION_DURATION, so it is still on screen after `collapsed` flips.
  // Card styling that has to agree with the body's presence keys off this instead, or the section
  // drops its elevation and re-rounds the header under a body that is still there.
  const [hasBody, setHasBody] = useState(!collapsed)
  useEffect(() => {
    if (!collapsed) return setHasBody(true)

    const timer = setTimeout(() => setHasBody(false), TRANSITION_DURATION)
    return () => clearTimeout(timer)
  }, [collapsed])

  // An opened section is usually taller than the gap it grew into, so on a long report the body a
  // reader just asked for can open below the fold. Bring it into view once the panel has finished
  // animating -- scrolling to a header that is still moving lands somewhere else by the time it
  // settles. `nearest` keeps an already-visible section still. Only a collapsed -> expanded
  // transition scrolls, so a section already open at mount never yanks the page.
  const sectionRef = useRef(null)
  const wasCollapsed = useRef(collapsed)
  useEffect(() => {
    const opening = wasCollapsed.current && !collapsed
    wasCollapsed.current = collapsed
    if (!opening) return

    const timer = setTimeout(
      () => sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }),
      TRANSITION_DURATION
    )
    return () => clearTimeout(timer)
  }, [collapsed])

  // A subtitle may carry its own link (PaidViewpoint's "Learn more"). The whole header is the
  // accordion's toggle, so without this a click on the link would also collapse the section.
  const onSubtitleClick = event => {
    if (event.target.closest('a')) event.stopPropagation()
  }

  const headerText = (
    <span className={style.headerText}>
      <div data-testid="stat-dqr-section-title" className={style.title}>
        {title}
      </div>
      {subtitle ? (
        <div
          data-testid="stat-dqr-section-subtitle"
          className={style.subtitle}
          onClick={onSubtitleClick}
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />
      ) : null}
    </span>
  )
  // Analytics tags clicks by plain class names, never the hashed module classes: which section the header and
  // body belong to, and whether it is closed at click time, so a click on a closed header counts as an open.
  // The body is mounted only while open or animating shut, so it reads closed only during that animation.
  const analyticsName = SECTION_ANALYTICS_NAME[section]
  const header = (
    <div
      data-testid="stat-dqr-section-header"
      className={classNames(style.header, '-dtr-stat-dqr-section-header', {
        [`-dtr-stat-dqr-section-header--${analyticsName}`]: analyticsName,
        '-dtr-stat-dqr-section-header--open': !collapsed,
        '-dtr-stat-dqr-section-header--closed': collapsed
      })}
      onClick={locked ? undefined : onToggle}
    >
      {icon ? (
        <span data-testid="stat-dqr-section-icon" className={style.icon}>
          {icon}
        </span>
      ) : null}
      {fielding ? (
        <Popup className="PRAYTMTheme" trigger="hover" direction="top" portal content={renderFieldingTooltip}>
          {headerText}
        </Popup>
      ) : (
        <>
          {headerText}
          {failed ? (
            <Button
              data-testid="stat-dqr-section-retry"
              className={classNames(style.retry, 'p-button-sm p-button-bright-blue')}
              label="Retry"
              onClick={onRetry}
            />
          ) : null}
          {locked ? null : (
            <span data-testid="stat-dqr-section-caret" className={style.caret}>
              <i className="icon-aytmfd-chevron-bracket" />
            </span>
          )}
        </>
      )}
    </div>
  )

  return (
    <section
      ref={sectionRef}
      data-testid="stat-dqr-section"
      className={classNames(style.section, {
        [style.fielding]: locked,
        [style.expanded]: !collapsed,
        [style.hasBody]: hasBody
      })}
    >
      {header}
      <TransitionPanel in={!collapsed}>
        {status =>
          status === 'exited' ? null : (
            <div
              data-testid={section ? `stat-dqr-section-body-${section}` : 'stat-dqr-section-body'}
              className={classNames(style.body, '-dtr-stat-dqr-section-body', {
                [`-dtr-stat-dqr-section-body--${analyticsName}`]: analyticsName,
                '-dtr-stat-dqr-section-body--open': !collapsed,
                '-dtr-stat-dqr-section-body--closed': collapsed
              })}
            >
              {children}
            </div>
          )
        }
      </TransitionPanel>
    </section>
  )
}

SectionShell.displayName = 'SectionShell'
SectionShell.propTypes = {
  section: PropTypes.string,
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  state: PropTypes.oneOf([
    SECTION_STATE.ACTIVE,
    SECTION_STATE.FIELDING_IN_PROGRESS,
    SECTION_STATE.LOADING_DATA,
    SECTION_STATE.LOAD_FAILED
  ]).isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onRetry: PropTypes.func,
  children: PropTypes.node
}
SectionShell.defaultProps = { subtitle: null, icon: null, expanded: false, onToggle: () => {}, onRetry: () => {} }
export default SectionShell
